import { app, connectivity } from "../../feathers";
import { TranslateJSON } from "Js/bfUtilities";
// import { actions } from "Js/actionsProcessor";
import { cacheRegListeners, cacheAppLoad } from "Js/cacher.js"

// import _ from "lodash";
// import VueRouter from "vue-router";

import { Injector } from "Js/injector";
import {
  restoreGlobalSchemaPreview,
  readTestModeFromSession,
  writeTestModeToSession,
  clearTestModeFromSession
} from '../../globalSchemaLayout.js';
import { bootCssPacks } from '../../cssPacks.js';

var injector = new Injector({
  container: document.getElementsByTagName("head")[0]
});

const edit2DiagStartedAt = typeof performance !== 'undefined' ? performance.now() : 0;

function sendEdit2BootDiag(stage, details = {}) {
  if (typeof window === 'undefined' || new URLSearchParams(window.location.search).get('_bfEdit2Diag') !== '1') return;
  try {
    window.parent.postMessage(JSON.stringify({
      __bfEdit2Diag: true,
      stage,
      elapsedMs: typeof performance !== 'undefined' ? Math.round(performance.now() - edit2DiagStartedAt) : 0,
      details
    }), '*');
  } catch (e) {
    return;
  }
}

function safeErrorDetails(error) {
  return {
    name: error && error.name || typeof error,
    code: error && error.code || null,
    className: error && error.className || null
  };
}

function isValidBfSiteCache(result) {
  return Boolean(
    result &&
    result.siteJSON &&
    result.siteJSON.id &&
    result.siteJSON.content &&
    result.siteJSON.content.layouts &&
    typeof result.siteJSON.content.layouts === 'object'
  );
}

function safeLocalStorageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeLocalStorageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
}

function safeLocalStorageRemove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    // Storage may be blocked; recovery should continue without cache access.
  }
}

function readBfSiteCache() {
  try {
    const raw = safeLocalStorageGet('_bfsite');
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch (e) {
    safeLocalStorageRemove('_bfsite');
    return null;
  }
}

function clearInvalidBfSiteCache() {
  safeLocalStorageRemove('_bfsite');
}

export default {
  state: {
    loaded: false,
    theme: "theme-green-full",
    showProfile: false,
    sidebarOpen: false,
    sidebarMini: false,
    showSideNavBar: false,
    showTopNavBar: true,
    showTopNavBarNoAuth: false,
    showSideNavBarNoAuth: false,
    errors: [],
    components:[],
    app: {},
    params: {},
    content: { 
      app: {},
      appCaching: [] },
    pendingThreads: [],
    isOnline: false,
    globalSchemaTestMode: null // GLOBAL_SCHEMA_DEV_PREVIEW: remove when dev toggle panel is removed
  },

  actions: {
    LOAD_SITE_MODEL: ({ commit, state, dispatch }) => {
      sendEdit2BootDiag('boot:site-load:start', {
        navigatorOnline: navigator.onLine,
        stateOnline: state.isOnline
      });
      dispatch('ACTIONS_PAUSE', true);
      return new Promise((resolve, reject) => {
        (async () => {
        // STEP 9: SSR HYDRATION - Check for server-rendered initial state
        if (window.__INITIAL_STATE__ && window.__INITIAL_STATE__.siteModel) {
          console.log('[SSR Hydration] Using server-rendered site model');
          const ssrState = window.__INITIAL_STATE__;
          const site = ssrState.siteModel;
          const bfops = { siteJSON: site, statusLS: false };
          if (!isValidBfSiteCache(bfops)) {
            sendEdit2BootDiag('boot:site-load:reject', { reason: 'invalid-ssr-site' });
            delete window.__INITIAL_STATE__;
            reject("No site record found for this domain");
            return;
          }
          
          // Store in localStorage for future offline use
          if (safeLocalStorageSet('_bfsite', JSON.stringify(bfops))) {
            console.log('[SSR Hydration] Cached site model to localStorage');
          } else {
            console.warn('[SSR Hydration] Failed to cache to localStorage');
          }
          
          // Process header insertions (they were already injected server-side, but client needs to know)
          // Skip the injector.insert() since headers are already in the DOM from SSR
          
          // Load the site using the standard flow
          loadSite({resolve, reject, commit, dispatch}, site);
          
          // Clean up the SSR state to save memory
          delete window.__INITIAL_STATE__;
          
          return; // Exit early, no need for network calls
        }
        
        // Normal CSR flow (no SSR)
        var bfops = readBfSiteCache();
        const bootstrapPromise = window.__bfSiteBootstrapPromise;
        if (
          !isValidBfSiteCache(bfops) &&
          navigator.onLine &&
          bootstrapPromise &&
          typeof bootstrapPromise.then === 'function'
        ) {
          const bootstrapWaitStartedAt = typeof performance !== 'undefined' ? performance.now() : 0;
          sendEdit2BootDiag('boot:site-bootstrap-wait:start');
          let bootstrapResult;
          try {
            bootstrapResult = await bootstrapPromise;
          } catch (error) {
            bootstrapResult = {
              status: 'rejected',
              errorName: error && error.name || typeof error
            };
          }
          bfops = readBfSiteCache();
          sendEdit2BootDiag('boot:site-bootstrap-wait:end', {
            status: bootstrapResult && bootstrapResult.status || null,
            errorName: bootstrapResult && bootstrapResult.errorName || null,
            cacheValid: isValidBfSiteCache(bfops),
            waitMs: typeof performance !== 'undefined' ? Math.round(performance.now() - bootstrapWaitStartedAt) : 0
          });
        }
        var hash = bfops && bfops.siteJSON && bfops.siteJSON.hash || '';
        var hadInvalidCache = Boolean(bfops && !isValidBfSiteCache(bfops));
        sendEdit2BootDiag('boot:site-cache-read', {
          cachePresent: Boolean(bfops),
          cacheValid: Boolean(bfops && !hadInvalidCache),
          hashPresent: Boolean(hash)
        });

        if (hadInvalidCache) {
          clearInvalidBfSiteCache();
          hash = '';
        }

        var cacheLoaded = false;
        const loadCachedSite = async () => {
          if (cacheLoaded || !bfops || hadInvalidCache) return;
          sendEdit2BootDiag('boot:site-cache-load:start');
          var site = await domHeaderInsertions(resolve, reject, bfops);
          if (site) {
            sendEdit2BootDiag('boot:site-cache-load:ready');
            loadSite({ resolve, reject, commit, dispatch }, site);
            cacheLoaded = true;
          }
        };

        // Forms and auth read isOnline as soon as the site resolves, so wait for the
        // socket first unless the browser reports offline (PWA offline boot).
        if (!navigator.onLine) {
          await loadCachedSite();
        }

        var onlineWaitStartedAt = typeof performance !== 'undefined' ? performance.now() : 0;
        var online = false;
        try {
          await connectivity.waitForOnline();
          online = true;
        } catch (_) {
          online = false;
        }
        sendEdit2BootDiag('boot:site-online-wait:end', {
          stateOnline: online,
          connectivityState: connectivity.getState(),
          navigatorOnline: navigator.onLine,
          waitMs: typeof performance !== 'undefined' ? Math.round(performance.now() - onlineWaitStartedAt) : 0
        });

        await loadCachedSite();

        if (online) {
          loadSiteFromNetwork(resolve, reject, hash);
          return;
        }
        if (!cacheLoaded) {
          sendEdit2BootDiag('boot:site-load:reject', { reason: 'no-cache-and-offline' });
          reject("No site record found for this domain");
        }
        })().catch(error => {
          sendEdit2BootDiag('boot:site-load:error', safeErrorDetails(error));
          reject(error);
        });
      });
    },

    ERROR_PUSH: ({ commit, state, dispatch }, errObj) => {
      commit("setError", errObj);
      return;
    },

    ERROR_CLEAR: ({ commit, state, dispatch }) => {
      commit("clearError");
      return;
    }
  },

  getters: {
    showTopNavBar: (state, getters, rootState, rootGetters) => {
      return state.showTopNavBar && (state.showTopNavBarNoAuth || rootState.auth.authenticated)
    },

    showSideNavBar: (state, getters, rootState, rootGetters) => {
      return state.showSideNavBar && (state.showSideNavBarNoAuth || rootState.auth.authenticated)
    },
    
    components: (state, getters, rootState, rootGetters) => name => {
      return _.find(state.content.components, { 'name': name }) || _.find(state.components, { 'name': name }); 
    },
    
    getSiteNamedAction: (state, getters) => (name) => {
      // 1) Site-level namedActions
      const fromSite = _.get(state.content, ['namedActions', name]);
      if (fromSite) return fromSite;

      // 2) Bare name: scan component list once (O(N)).
      // If performance becomes a concern, pre-index on SET_SITE.
      const contentComponents = _.get(state, ['content', 'components']) || [];
      const rootComponents = state.components || [];
      const list = contentComponents.concat(rootComponents);
      for (let i = 0; i < list.length; i++) {
        const candidate = _.get(list[i], ['namedActions', name]) || _.get(list[i], ['comp', 'namedActions', name]);
        if (candidate) return candidate;
      }
      return undefined;
    },
    
    pendingThreads: (state, getters, rootState, rootGetters) => {
      return state.pendingThreads; 
    },
    
    isOnline: (state, getters, rootState, rootGetters) => name => {
      return state.isOnline;
    },
    
    getForm: (state, getters, rootState, rootGetters) => id => {
      return false;
    },
    
    // Global Schema - returns the appropriate layout schema for the current route
    getCurrentLayoutSchema: (state) => (route) => {
      const globalSchema = state.content && state.content.globalSchema;
      
      // If no globalSchema is configured, return null (backward compatibility)
      if (!globalSchema) return null;
      
      // Import route matching utility
      const { findMatchingSchema } = require('../../routePatternMatcher.js');
      
      // Find the best matching route schema (already handles default fallback)
      return findMatchingSchema(globalSchema, route);
    }
  },

  mutations: {
    setError: (state, errObj) => {
      state.errors.unshift(errObj);
    },

    clearError: (state) => {
      state.errors = []
    },

    SET_APP: (state, appObject) => {
      state.content.app = Object.assign({}, state.content.app, appObject);
    },

    SET_PARAMS: (state, paramsObject) => {
      state.content.params = Object.assign({},
        state.content.params,
        paramsObject
      );
    },

    SET_SITE: (state, site) => {
      let newObj = Object.assign({}, state, site);
      newObj.loaded = true;
      Vue.set(vueapp.$store.state, "site", newObj); // because reactivity breaks
      
    },

    SET_GLOBAL_SCHEMA_TEST_MODE: (state, mode) => {
      // GLOBAL_SCHEMA_DEV_PREVIEW: dev-only instant legacy/global toggle; see Internal Docs/global-schema-feature/GLOBAL_SCHEMA_DEV_PREVIEW_REMOVAL.md
      state.globalSchemaTestMode = (mode === 'legacy' || mode === 'global') ? mode : null;
      if (state.globalSchemaTestMode) {
        writeTestModeToSession(state.globalSchemaTestMode);
      } else {
        clearTestModeFromSession();
      }
    },

    SET_SIDEBAROPEN(state, mode) {
      state.sidebarOpen = mode;
    },

    SET_SIDEBARMINI(state, mode) {
      state.sidebarMini = mode;
    },

    SET_PENDINGTRHEADS: (state, idThread) => {
      state.pendingThreads.length ?
        state.pendingThreads.push(idThread) :
        state.pendingThreads[0] = idThread;
    },
    
    CLEAR_PENDINGTHREADS: (state) => {
      state.pendingThreads = [];
    },
    
    SET_ISONLINE(state, status) {
      if (state.isOnline !== status && status) {
        if (window.vueapp && window.vueapp.$store) {
          window.vueapp.$store.dispatch('ACTIONS_QUEUE', {actions: [{"action": "namedAction","name": "appIsOnline"}]})
        }
      }
      else if (state.isOnline !== status && !status) {
        if (window.vueapp && window.vueapp.$store) {
          window.vueapp.$store.dispatch('ACTIONS_QUEUE', {actions: [{"action": "namedAction","name": "appIsOffline"}]})
        }
      }
      
      state.isOnline = status;
    },

    // Register component-scoped namedActions into site.content.namedActions
    REGISTER_COMPONENT_ACTIONS: (state, { actions, source, overwrite, namespace }) => {
      if (!actions || typeof actions !== 'object') return;
      // Ensure bucket exists and is reactive
      if (!state.content.namedActions) {
        Vue.set(state.content, 'namedActions', {});
      }
      Object.keys(actions).forEach((key) => {
        // Compute a namespaced key to avoid collisions unless the key already looks namespaced
        const looksNamespaced = key.includes('.') || key.includes(':');
        const ns = namespace || source;
        const finalKey = (ns && !looksNamespaced) ? `${ns}.${key}` : key;

        if (!overwrite && state.content.namedActions[finalKey]) {
          return; // skip existing by default
        }
        // Assign action definition under its key
        Vue.set(state.content.namedActions, finalKey, actions[key]);
      });
    }
    
  },
  
  loadSiteFromNetwork
};

function domHeaderInsertions (resolve, reject, result) {
  if (result.statusLS) {
    result = readBfSiteCache();
  }
  else if (isValidBfSiteCache(result)) {
    const serializedSite = JSON.stringify(result);
    // Cached startup already persisted this content; avoid an identical rewrite.
    if (safeLocalStorageGet('_bfsite') !== serializedSite &&
        !safeLocalStorageSet('_bfsite', serializedSite)) {
      BF.errorThrow (10171, `Unable to add '_bfsite' to local storage.`, 'Error encountered while attempting to write data to local or session storage.');
    }
  }
  
  if (!isValidBfSiteCache(result)) {
    sendEdit2BootDiag('boot:header-insertions:reject', { reason: 'invalid-site-cache' });
    reject("No site record found for this domain");
    return null;
  }

  //TRANSLATE
  var site = result.siteJSON;
  TranslateJSON(site, 0); //site uses parent wndw[0] for calcs

  // DOM INSERTIONS SYNC
  if (_.get(site, "content.headerInsertions")) {
    sendEdit2BootDiag('boot:header-insertions:start');
    return new Promise((resolve, reject) => {
        injector.oncomplete = function() {
          sendEdit2BootDiag('boot:header-insertions:end');
          resolve(site)
        };
        injector.insert(site.content.headerInsertions);
      })
      .then(site => {
        injector.oncomplete = function(){};
        return site
      })
  }
  else {
    sendEdit2BootDiag('boot:header-insertions:skipped');
    return site
  }
}

function loadSite ({resolve, reject, commit, dispatch}, site) {
    sendEdit2BootDiag('boot:site-commit:start', {
      hasDeferredInsertions: Boolean(_.get(site, "content.headerInsertionsDefer"))
    });
    const queueOnAppLoad = () => {
      if (_.get(site, "content.namedActions.onAppLoad") && BF.getQueryParam('_onAppLoad')!=='0' ) {
        dispatch('ACTIONS_QUEUE', site.content.namedActions.onAppLoad);
      }
    };

    // Defer onAppLoad until deferred header scripts have actually finished loading.
    if (_.get(site, "content.headerInsertionsDefer")) {
      injector.oncomplete = function() {
        injector.oncomplete = function(){};
        queueOnAppLoad();
      };
      injector.insert(site.content.headerInsertionsDefer);
    } else {
      queueOnAppLoad();
    }
    
    // CSS
    if (site.css) {
      var style = document.createElement("style");
      style.appendChild(document.createTextNode(site.css));
      document.head.appendChild(style);
    }

    if (site.content) {
      site.content = restoreGlobalSchemaPreview(site.content); // GLOBAL_SCHEMA_DEV_PREVIEW
    }
    
    commit("SET_SITE", site);
    bootCssPacks(site.content);

    const restoredTestMode = readTestModeFromSession(); // GLOBAL_SCHEMA_DEV_PREVIEW
    if (restoredTestMode) {
      commit('SET_GLOBAL_SCHEMA_TEST_MODE', restoredTestMode);
    }
    
    cacheRegListeners() // cahcing after model is mutated
  
    // need to wait for actions to finish running
  
    //Hydrate any cachable app keys
    cacheAppLoad()
    sendEdit2BootDiag('boot:site-load:resolved');
    resolve(site);
}

async function loadSiteFromNetwork (resolve, reject, hash) {
  sendEdit2BootDiag('boot:site-request:start', { hashPresent: Boolean(hash) });
  return app
  .service("/site")
  .find({
    query: {
      active: 1,
      hashLS: hash
    }
  })
  .then(async result => {
        sendEdit2BootDiag('boot:site-request:result', {
          statusLS: Boolean(result && result.statusLS),
          validSite: isValidBfSiteCache(result)
        });
        if (!result.statusLS) {
          if (!isValidBfSiteCache(result)) {
            sendEdit2BootDiag('boot:site-load:reject', { reason: 'invalid-network-site' });
            reject("No site record found for this domain");
            return;
          }
          //Save error page locally everytime _bfsite is saved on LS
          sendEdit2BootDiag('boot:error-form-request:start');
          var errorForm = await app.service("forms").get('FR_errorpage');
          sendEdit2BootDiag('boot:error-form-request:end', { received: Boolean(errorForm) });
          _.set(result.siteJSON.content.layouts, 'error.formSchema', errorForm)
          const cacheSaved = safeLocalStorageSet('_bfsite', JSON.stringify(result));
          sendEdit2BootDiag('boot:site-cache-write', { saved: cacheSaved });
          if (!cacheSaved) {
            BF.errorThrow (10171, `Unable to add '_bfsite' to local storage.`, 'Error encountered while attempting to write data to local or session storage.');
          }
          sendEdit2BootDiag('boot:site-reload:requested');
          window.location.reload();
        }
        sendEdit2BootDiag('boot:site-request:resolved');
        resolve();
    }
  )
  .catch(error => {
    sendEdit2BootDiag('boot:site-request:error', safeErrorDetails(error));
    console.log("@Store.site - siteLoad_Error", error);
    reject(error);
  });
}
